import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import MedicationService from "../../../../../services/medicationService";
import { debounce } from "lodash";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";

interface MedicationOption {
  name: string;
  strength?: string;
  brandName?: string;
  genericName?: string;
  form?: string;
  description?: string;
  photonMedicationId?: string;
  isCustomOption?: boolean;
}

interface MedicationsAutoCompleteProps {
  name: string;
  updateOtherFields?: boolean;
}

const MedicationsAutoComplete: React.FC<MedicationsAutoCompleteProps> = ({ name, updateOtherFields = true }) => {
  const prefix = name.split(".").slice(0, -1).join(".");
  const [options, setOptions] = useState<MedicationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const { getValues, setValue } = useFormContext();
  const [inputValue, setInputValue] = useState(getValues(`${prefix}.name`));

  // Create memoized debounced search function
  const searchMedications = debounce(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await MedicationService.searchMedications(searchQuery);
      setOptions(response.data);
    } catch (error) {
      console.error("Error searching medications:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  const getOptionLabel = (option: MedicationOption) => {
    if (!option) return "";
    return option.brandName || option.genericName || option.name;
  };

  // This ensures each option has a stable, unique key
  const getOptionKey = (option: MedicationOption) => {
    if (option.isCustomOption) return `custom-${option.name}`;
    return option.photonMedicationId || `med-${option.name}-${option.strength || ''}`;
  };

  const renderOption = (props: any, option: MedicationOption) => {
    if (!option) return null;
    
    if (option.isCustomOption) {
      return (
        <li {...props} key={`custom-${option.name}`}>
          <Typography variant="body1">
            Use custom: <span style={{ fontWeight: "bold" }}>{option.name}</span>
          </Typography>
        </li>
      );
    }

    const displayName = option.brandName || option.genericName || option.name;
    const strength = option.strength ? ` - ${option.strength}` : "";
    const form = option.form ? ` (${option.form})` : "";

    return (
      <li {...props} key={getOptionKey(option)}>
        <Typography variant="body1">
          <strong>{displayName}</strong>
          {strength}
          {form}
        </Typography>
      </li>
    );
  };

  // Combine API options with custom option
  const allOptions = inputValue && inputValue.length >= 3
    ? [{ name: inputValue, isCustomOption: true as const }, ...options]
    : options;

  const noOptionsText = inputValue?.length >= 3 
    ? "No options" 
    : !inputValue 
      ? "Type to search for medications" 
      : "Keep typing to search for medications";
  return (
    <Autocomplete<MedicationOption, false, false, false>
      options={allOptions}
      getOptionLabel={getOptionLabel}
      loading={loading}
      filterOptions={(options) => options} // Disable built-in filtering - rely on API
      onChange={(_, value) => {
        if (!value) {
          setValue(`${prefix}.name`, "");
          return;
        }
        
        if (updateOtherFields) {
          Object.entries(value).forEach(([key, value]) => {
            setValue(`${prefix}.${key}`, value);
          });
        } else {
          setValue(`${prefix}.name`, value.name || "");
        }
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        searchMedications(newInputValue);
      }}
      isOptionEqualToValue={(option, value) => {
        // Proper comparison to help Autocomplete track selections correctly
        if (!option || !value) return false;
        
        if (option.isCustomOption && value.isCustomOption) {
          return option.name === value.name;
        }
        
        return option.photonMedicationId === value.photonMedicationId || 
               option.name === value.name && option.strength === value.strength;
      }}
      noOptionsText={noOptionsText}
      renderOption={renderOption}
      selectOnFocus
      clearOnBlur
      defaultValue={getValues(prefix)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Medication Name"
          sx={{ marginY: 0, backgroundColor: "background.paper" }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MedicationsAutoComplete;